// src/context/UserContext.tsx
import { notify } from "@components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
} from "@lib/storeManager";
import { AuthService, UserPreferencesService } from "@/services/APIs";
import { AuthRes, SocialLoginRes } from "@/utils/types/auth";
import { UserPreferenceItem } from "@/utils/types/userPreferencesGrid";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

interface DecodedToken {
  userId: string;
  email: string;
  exp: number;
  [key: string]: any;
}

interface UserContextType {
  jwtToken: string | null;
  refreshToken: string | null;
  decodedToken: DecodedToken | null;
  isAuthenticated: boolean;
  preferencesData: UserPreferenceItem[] | null;
  fetchPreferencesData: () => Promise<void>;
  signup: (
    email: string,
    password: string,
    first_name: string,
    last_name: string
  ) => Promise<any>;
  login: (email: string, password: string) => Promise<AuthRes | undefined>;
  logout: () => Promise<any>;
  socialLogin: (provider: string) => Promise<SocialLoginRes | undefined>;
  forgotPassword: (email: string) => Promise<any>;
  forgotPasswordReset: (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => Promise<any>;
  resetPassword: (values: {
    current_password: string;
    new_password: string;
  }) => Promise<any>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Local state
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const [preferencesData, setPreferencesData] = useState<
    UserPreferenceItem[] | null
  >(null);

  // Bootstrap from localStorage
  useEffect(() => {
    const access = getAccessToken();
    const refresh = getRefreshToken();
    if (access && refresh) {
      try {
        const decoded = jwtDecode<DecodedToken>(access);
        setJwtToken(access);
        setRefreshToken(refresh);
        setDecodedToken(decoded);
      } catch {
        clearLocalStorage();
      }
    }
  }, []);

  const isAuthenticated =
    Boolean(jwtToken && decodedToken) && decodedToken!.exp * 1000 > Date.now();

  // Fetch preferences data function
  const fetchPreferencesData = useCallback(async () => {
    const data = await UserPreferencesService.getUserPreferencesGrid();
    if (data) {
      setPreferencesData(data);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferencesData();
    }
  }, [isAuthenticated, fetchPreferencesData]);

  const scheduleRefresh = useCallback(() => {
    if (!decodedToken || !refreshToken) {
      return;
    }
    const expiresAt = decodedToken.exp * 1000;
    const timeout = expiresAt - Date.now() - 60_000;
    if (timeout <= 0) {
      return;
    }

    const handle = window.setTimeout(async () => {
      try {
        const response = await AuthService.refreshToken();
        setJwtToken(response.access);
        setDecodedToken(jwtDecode<DecodedToken>(response.access));
        scheduleRefresh();
      } catch {
        await handleLogout();
      }
    }, timeout);

    return () => clearTimeout(handle);
  }, [decodedToken, refreshToken]);

  useEffect(() => {
    const cleanup = scheduleRefresh();
    return cleanup;
  }, [scheduleRefresh]);

  const handleSignup = async (
    userEmail: string,
    userPassword: string,
    userFirstName: string,
    userLastName: string
  ) => {
    const response = await AuthService.signup({
      email: userEmail,
      password: userPassword,
      first_name: userFirstName,
      last_name: userLastName,
    });
    type TokenPayload = { access: string; refresh: string };
    const payload = (response?.data ?? response) as TokenPayload;
    const access = payload?.access;
    const refresh = payload?.refresh;

    if (response) {
      setJwtToken(access);
      setRefreshToken(refresh);
      const decoded = jwtDecode<DecodedToken>(access);
      setDecodedToken(decoded);
      setLocalAccessToken(access);
      setRefreshToken(refresh);
      setLocalRefreshToken(refresh);
      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;
      setLocalUserInfo({
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      });
      fetchPreferencesData();
      return response;
    }
  };

  const handleLogin = async (userEmail: string, userPassword: string) => {
    const response = await AuthService.login({
      email: userEmail,
      password: userPassword,
    });
    if (response) {
      setJwtToken(response.access);
      setRefreshToken(response.refresh);

      const decoded = jwtDecode<DecodedToken>(response.access);
      setDecodedToken(decoded);
      setLocalAccessToken(response.access);
      setRefreshToken(response.refresh);
      setLocalRefreshToken(response.refresh);
      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;
      setLocalUserInfo({
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      });
      fetchPreferencesData();
      return response;
    }
  };

  const handleForgotPassword = async (userEmail: string) => {
    const response = await AuthService.forgotPassword(userEmail);
    if (response) {
      notify.success("Reset password email sent successfully");
      return response;
    }
  };

  const handleForgotPasswordReset = async (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => {
    const response = await AuthService.forgotPasswordReset(id, payload);
    if (response) {
      notify.success("Password reset successfully");
      return response;
    }
  };

  const handleLogout = async () => {
    const response = await AuthService.logout();
    setJwtToken(null);
    setRefreshToken(null);
    setDecodedToken(null);
    clearLocalStorage();
    window.location.reload();
    return response;
  };

  const handleResetPassword = async (values: {
    current_password: string;
    new_password: string;
  }) => {
    const response = await AuthService.resetPassword(values);
    return response;
  };

  const handleSocialLogin = async (provider: string) => {
    const response = await AuthService.socialLogin(provider);
    return response;
  };

  return (
    <UserContext.Provider
      value={{
        jwtToken,
        refreshToken,
        decodedToken,
        isAuthenticated,
        preferencesData,
        fetchPreferencesData,
        signup: handleSignup,
        login: handleLogin,
        logout: handleLogout,
        socialLogin: handleSocialLogin,
        forgotPassword: handleForgotPassword,
        forgotPasswordReset: handleForgotPasswordReset,
        resetPassword: handleResetPassword,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Export the context for use in other files
export { UserContext };
